import React from 'react';
import { StandardTableActions, ColoredBadge } from '../../components/ui';
import { Link } from 'react-router-dom';
import { Column } from '../../components/DataTable/DataTable';
import { TruncatedText } from '../../components/DataTable/TruncatedText';
import { ClientInvoice } from './useData';
import { FieldConfig } from '../../components/generic/fieldRenderers';
import { FormLayout } from '../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../config/appConfig';
import { ThemeColors } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import dayjs from 'dayjs';
import { EntityAttachmentsSection } from '../../components/EntityAttachmentsSection';
import toast from 'react-hot-toast';
import { renderInvoicePaymentReminderStatusBadge } from './ClientInvoicePaymentRemindersSection';
import { ClientInvoicePaymentRemindersSection } from './ClientInvoicePaymentRemindersSection';

// Format currency helper
const formatCurrency = (amount?: number | null) => {
  if (amount === null || amount === undefined) return '-';
  return new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export type PaymentStatusKind = 'Unpaid' | 'Partial' | 'Paid';

const INVOICE_PAYMENT_STATUS_COLORS: Record<PaymentStatusKind, string> = {
  Unpaid: '#ef4444',
  Partial: '#eab308',
  Paid: '#22c55e',
};

const getPaymentStatus = (
  paymentTotal: number | null,
  invoiceAmount: number,
  formatPayment?: (amount: number) => string
): { label: string; kind: PaymentStatusKind } => {
  if (paymentTotal === null) {
    return { label: 'Unpaid', kind: 'Unpaid' };
  }
  if (paymentTotal < invoiceAmount) {
    const label = formatPayment ? `Partial (${formatPayment(paymentTotal)})` : 'Partial';
    return { label, kind: 'Partial' };
  }
  return { label: 'Paid', kind: 'Paid' };
};

const renderInvoicePaymentStatusBadge = (
  label: string,
  kind: PaymentStatusKind,
  className?: string
) => (
  <ColoredBadge label={label} color={INVOICE_PAYMENT_STATUS_COLORS[kind]} className={className} />
);

// Module Metadata
export const moduleConfig = {
  // Module Information
  module: {
    title: 'Client Invoices',
    description: 'Manage client invoices database',
    permissionModuleKey: 'client_invoices',
    route: {
      list: '/client-invoices',
      new: '/client-invoices/new',
      detail: (id: number) => `/client-invoices/${id}`,
      edit: (id: number) => `/client-invoices/${id}/edit`,
    },
    // API Endpoints
    api: {
      list: '/crm-clients-invoices',
      detail: (id: number) => `/crm-clients-invoices/${id}`,
    },
  },

  // Pagination Settings
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },

  // Search Settings
  search: {
    placeholder: 'Search invoices by client, number, type, or category...',
  },

  // Filter Groups Configuration
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: [
        'clientName', 'InvoiceNo', 'invoiceTypeDescr', 'invoiceCategoryDescr', 'status'
      ],
    },
    {
      name: 'financial',
      label: 'Financial',
      icon: '💰',
      fields: [
        'invoiceAmount', 'invoiceDiscount', 'invoiceVAT', 'invoiceIncome'
      ],
    },
    {
      name: 'dates',
      label: 'Dates',
      icon: '📅',
      fields: [
        'InvoiceDate', 'invoiceDue', 'overdueDays'
      ],
    },
  ],

  // Filter Configuration
  filters: {
    fields: [
      {
        key: 'clientName',
        label: 'Client',
        placeholder: 'All Clients',
        type: 'select' as const,
        getValue: (item: ClientInvoice) => item.client?.clientName || (item.clientId ? `Client #${item.clientId}` : ''),
        options: [],
      },
      {
        key: 'InvoiceNo',
        label: 'Invoice Number',
        placeholder: 'All Invoice Numbers',
        getValue: (item: ClientInvoice) => item.InvoiceNo || '',
        options: [],
      },
      {
        key: 'invoiceTypeDescr',
        label: 'Type',
        placeholder: 'All Types',
        type: 'select' as const,
        getValue: (item: ClientInvoice) => item.invoiceTypeDescr?.pit_abbrev ?? '',
        options: [],
      },
      {
        key: 'invoiceCategoryDescr',
        label: 'Category',
        placeholder: 'All Categories',
        type: 'select' as const,
        getValue: (item: ClientInvoice) => item.invoiceCategoryDescr?.pic_descr || '',
        options: [],
      },
      {
        key: 'status',
        label: 'Status',
        placeholder: 'All Status',
        type: 'select' as const,
        getValue: (item: ClientInvoice) => {
          const invoiceAmount = parseFloat(String(item.invoiceAmount || 0));
          const paymentTotal = item.paymentTotal !== null ? parseFloat(String(item.paymentTotal)) : null;
          if (paymentTotal === null) return 'Unpaid';
          if (paymentTotal < invoiceAmount) return 'Partial';
          if (paymentTotal >= invoiceAmount) return 'Paid';
          return 'Unpaid';
        },
        options: ['Paid', 'Partial', 'Unpaid'],
      },
      {
        key: 'invoiceAmount',
        label: 'Amount',
        placeholder: 'All Amounts',
        type: 'number' as const,
        getValue: (item: ClientInvoice) => item.invoiceAmount?.toString() || '',
        options: [],
      },
      {
        key: 'invoiceDiscount',
        label: 'Discount',
        placeholder: 'All Discounts',
        type: 'number' as const,
        getValue: (item: ClientInvoice) => item.invoiceDiscount?.toString() || '',
        options: [],
      },
      {
        key: 'invoiceVAT',
        label: 'VAT',
        placeholder: 'All VAT',
        type: 'number' as const,
        getValue: (item: ClientInvoice) => item.invoiceVAT?.toString() || '',
        options: [],
      },
      {
        key: 'invoiceIncome',
        label: 'Income',
        placeholder: 'All Income',
        type: 'number' as const,
        getValue: (item: ClientInvoice) => item.invoiceIncome?.toString() || '',
        options: [],
      },
      {
        key: 'InvoiceDate',
        label: 'Invoice Date',
        placeholder: 'All Invoice Dates',
        type: 'date' as const,
        getValue: (item: ClientInvoice) =>
          item.InvoiceDate
            ? formatDateGlobal(item.InvoiceDate)
            : undefined,
        options: [],
      },
      {
        key: 'invoiceDue',
        label: 'Due Date',
        placeholder: 'All Due Dates',
        type: 'date' as const,
        getValue: (item: ClientInvoice) =>
          item.invoiceDue
            ? formatDateGlobal(item.invoiceDue)
            : undefined,
        options: [],
      },
      {
        key: 'overdueDays',
        label: 'Overdue Days',
        placeholder: 'All Overdue Days',
        type: 'number' as const,
        getValue: (item: ClientInvoice) => item.overdueDays?.toString() || '',
        options: [],
      },
    ],
    // Filter logic functions
    applyFilter: (
      items: ClientInvoice[],
      filters: {
        clientName: string[] | string;
        InvoiceNo: string[] | string;
        invoiceTypeDescr: string[];
        invoiceCategoryDescr: string[];
        status: string[];
        invoiceAmount: string[];
        invoiceDiscount: string[];
        invoiceVAT: string[];
        invoiceIncome: string[];
        InvoiceDate: string[];
        invoiceDue: string[];
        overdueDays: string[];
      },
      dateRanges?: Record<string, { start: Date | null; end: Date | null }>
    ) => {
      const ranges = dateRanges || {};
      return items.filter((item) => {
        // Client name filter (matches by display name or ID)
        if (filters.clientName && (typeof filters.clientName === 'string' ? filters.clientName.trim() !== '' : filters.clientName.length > 0)) {
          const clientFilter = filters.clientName;
          const displayValue = item.client?.clientName || (item.clientId ? `Client #${item.clientId}` : '');
          if (typeof clientFilter === 'string') {
            const value = displayValue.toLowerCase();
            if (!value.includes(clientFilter.toLowerCase())) return false;
          } else if (Array.isArray(clientFilter)) {
            const matches = clientFilter.some((filter) => {
              return displayValue === filter || 
                     (item.clientId && filter === `Client #${item.clientId}`) ||
                     (item.client?.clientName && filter === item.client.clientName);
            });
            if (!matches) return false;
          }
        }

        // Invoice number filter
        const invoiceNoFilter = filters.InvoiceNo;
        if (typeof invoiceNoFilter === 'string' && invoiceNoFilter.trim() !== '') {
          const value = (item.InvoiceNo || '').toLowerCase();
          if (!value.includes(invoiceNoFilter.toLowerCase())) return false;
        } else if (Array.isArray(invoiceNoFilter) && invoiceNoFilter.length > 0) {
          const value = (item.InvoiceNo || '').toLowerCase();
          const matches = invoiceNoFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Type filter
        if (filters.invoiceTypeDescr.length > 0) {
          const value = (item.invoiceTypeDescr?.pit_abbrev ?? '').toLowerCase();
          const matches = filters.invoiceTypeDescr.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Category filter
        if (filters.invoiceCategoryDescr.length > 0) {
          const value = (item.invoiceCategoryDescr?.pic_descr || '').toLowerCase();
          const matches = filters.invoiceCategoryDescr.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Status filter
        if (filters.status.length > 0) {
          const invoiceAmount = parseFloat(String(item.invoiceAmount || 0));
          const paymentTotal = item.paymentTotal !== null ? parseFloat(String(item.paymentTotal)) : null;
          let status = '';
          if (paymentTotal === null) {
            status = 'Unpaid';
          } else if (paymentTotal < invoiceAmount) {
            status = 'Partial';
          } else if (paymentTotal >= invoiceAmount) {
            status = 'Paid';
          }
          if (!filters.status.includes(status)) return false;
        }

        // Amount filter
        if (filters.invoiceAmount.length > 0) {
          const value = item.invoiceAmount?.toString() || '';
          const matches = filters.invoiceAmount.some((filter) => {
            return value === filter || filter === value;
          });
          if (!matches) return false;
        }

        // Discount filter
        if (filters.invoiceDiscount.length > 0) {
          const value = item.invoiceDiscount?.toString() || '';
          const matches = filters.invoiceDiscount.some((filter) => {
            return value === filter || filter === value;
          });
          if (!matches) return false;
        }

        // VAT filter
        if (filters.invoiceVAT.length > 0) {
          const value = item.invoiceVAT?.toString() || '';
          const matches = filters.invoiceVAT.some((filter) => {
            return value === filter || filter === value;
          });
          if (!matches) return false;
        }

        // Income filter
        if (filters.invoiceIncome.length > 0) {
          const value = item.invoiceIncome?.toString() || '';
          const matches = filters.invoiceIncome.some((filter) => {
            return value === filter || filter === value;
          });
          if (!matches) return false;
        }

        // Invoice Date filter
        if (filters.InvoiceDate.length > 0) {
          if (ranges.InvoiceDate) {
            const range = ranges.InvoiceDate;
            if (range.start && range.end && item.InvoiceDate) {
              const itemDate = new Date(item.InvoiceDate);
              if (itemDate < range.start || itemDate > range.end) return false;
            }
          } else {
            const value = item.InvoiceDate
              ? formatDateGlobal(item.InvoiceDate)
              : '';
            const matches = filters.InvoiceDate.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) return false;
          }
        }

        // Due Date filter
        if (filters.invoiceDue.length > 0) {
          if (ranges.invoiceDue) {
            const range = ranges.invoiceDue;
            if (range.start && range.end && item.invoiceDue) {
              const itemDate = new Date(item.invoiceDue);
              if (itemDate < range.start || itemDate > range.end) return false;
            }
          } else {
            const value = item.invoiceDue
              ? formatDateGlobal(item.invoiceDue)
              : '';
            const matches = filters.invoiceDue.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) return false;
          }
        }

        // Overdue Days filter
        if (filters.overdueDays.length > 0) {
          const value = item.overdueDays?.toString() || '';
          const matches = filters.overdueDays.some((filter) => {
            return value === filter || filter === value;
          });
          if (!matches) return false;
        }

        return true;
      });
    },
  },

  // Toolbar Actions Configuration
  toolbar: {
    actions: [
      {
        key: 'download',
        icon: 'download',
        title: 'Download',
        showTitle: false,
        variant: 'default' as const,
        dropdown: [
          {
            key: 'export-excel',
            label: 'Export to Excel',
            icon: 'download',
            onClick: async (exportData?: {
              sortedData: any[];
              tableColumns: any[];
              entityName: string;
              exportFileName: string;
              formatDate?: (dateString?: string) => string;
            }) => {
              if (!exportData) {
                console.error('Export data not provided');
                return;
              }
              try {
                const { exportToExcel } = await import('../../components/exports');
                await exportToExcel(exportData);
              } catch (error: any) {
                console.error('Failed to export to Excel:', error);
                // Error is already logged to console
              }
            },
          },
        ],
      },
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Invoice',
        showTitle: false,
        variant: 'primary' as const,
        href: '/client-invoices/new',
      },
    ],
  },

  // Form Fields Configuration
  fields: [
    {
      name: 'clientId',
      label: 'Client',
      type: 'select',
      placeholder: 'Select a client',
      required: true,
      options: [], // Will be populated dynamically via API
      apiEndpoint: '/crm-clients', // API endpoint to fetch all client options
      labelField: 'clientName', // Field name in API response to use as label
      validation: {
        required: true,
      },
    },
    {
      name: 'supportAgreementId',
      label: 'Support Agreement (Optional)',
      type: 'select',
      placeholder: 'Select a support agreement (optional)',
      options: [], // Will be populated dynamically in Form.tsx
    },
    {
      name: 'InvoiceNo',
      label: 'Invoice Number',
      type: 'text',
      placeholder: 'Enter invoice number',
      validation: {
        maxLength: 255,
      },
    },
    {
      name: 'InvoiceDate',
      label: 'Invoice Date',
      type: 'date',
      placeholder: 'Select invoice date',
    },
    {
      name: 'invoiceType',
      label: 'Invoice Type',
      type: 'select',
      placeholder: 'Select invoice type',
      required: true,
      options: [], // Will be populated dynamically via API
      apiEndpoint: '/p-crm-invoice-type', // API endpoint to fetch all invoice type options
      labelField: 'pit_descr', // Field name in API response to use as label
      validation: {
        required: true,
      },
    },
    {
      name: 'InvoiceCategoryId',
      label: 'Invoice Category',
      type: 'select',
      placeholder: 'Select invoice category (optional)',
      options: [], // Will be populated dynamically via API
      apiEndpoint: '/p-crm-invoice-category', // API endpoint to fetch all invoice category options
      labelField: 'pic_descr', // Field name in API response to use as label
    },
    {
      name: 'invoiceAmount',
      label: 'Amount',
      type: 'number',
      placeholder: 'Enter amount',
      validation: {
        min: 0,
      },
    },
    {
      name: 'invoiceDiscount',
      label: 'Discount (%)',
      type: 'number',
      placeholder: 'Enter discount percentage',
      validation: {
        min: 0,
        max: 100,
      },
    },
    {
      name: 'invoiceVAT',
      label: 'VAT',
      type: 'number',
      placeholder: 'Enter VAT amount',
      validation: {
        min: 0,
      },
    },
    {
      name: 'invoiceIncome',
      label: 'Income',
      type: 'number',
      placeholder: 'Enter income amount',
      validation: {
        min: 0,
      },
    },
    {
      name: 'invoiceDue',
      label: 'Due Date',
      type: 'date',
      placeholder: 'Select due date',
    },
    {
      name: 'invoiceDescription',
      label: 'Description',
      type: 'textarea',
      rows: 6,
      placeholder: 'Enter invoice description',
      validation: {
        maxLength: 2000,
      },
    },
    {
      name: 'invoiceNotes',
      label: 'Notes',
      type: 'textarea',
      rows: 4,
      placeholder: 'Enter notes',
      validation: {
        maxLength: 1000,
      },
    },
  ] as FieldConfig[],

  // Form Layout Configuration (crm-tasks style: gridCols + fieldSpans)
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Basic Information',
        icon: '📄',
        gridCols: 'md:grid-cols-2',
        fields: ['clientId', 'supportAgreementId', 'InvoiceNo', 'InvoiceDate'],
        collapsible: true,
      },
      {
        id: 'type',
        title: 'Type & Category',
        icon: '🏷️',
        gridCols: 'md:grid-cols-2',
        fields: ['invoiceType', 'InvoiceCategoryId'],
        collapsible: true,
      },
      {
        id: 'financial',
        title: 'Financial Information',
        icon: '💰',
        gridCols: 'md:grid-cols-12',
        fields: ['invoiceAmount', 'invoiceDiscount', 'invoiceVAT', 'invoiceIncome', 'invoiceDue'],
        fieldSpans: {
          invoiceAmount: 'md:col-span-6',
          invoiceDiscount: 'md:col-span-6',
          invoiceVAT: 'md:col-span-6',
          invoiceIncome: 'md:col-span-6',
          invoiceDue: 'md:col-span-6',
        },
        collapsible: true,
      },
      {
        id: 'details',
        title: 'Details',
        icon: '📝',
        gridCols: 'md:grid-cols-12',
        fields: ['invoiceDescription', 'invoiceNotes'],
        fieldSpans: {
          invoiceDescription: 'md:col-span-12',
          invoiceNotes: 'md:col-span-12',
        },
        collapsible: true,
      },
    ],
  } as FormLayout,

  // Form CRUD Operations
  form: {
    new: {
      title: 'New Invoice',
      subtitle: 'Create a new client invoice',
      submitText: 'Create Invoice',
    },
    edit: {
      title: 'Edit Invoice',
      subtitle: 'Update invoice information',
      submitText: 'Update Invoice',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading invoice...',
    errorMessage: 'Failed to save invoice. Please try again.',
    supportsFiles: true,
    create: async (data: Partial<ClientInvoice>, files?: File[], filesToDetach?: number[]) => {
      const { createClientInvoice } = await import('../../services/mutations');
      return await createClientInvoice(data, files);
    },
    update: async (id: number, data: Partial<ClientInvoice>, files?: File[], filesToDetach?: number[]) => {
      const { updateClientInvoice } = await import('../../services/mutations');
      return await updateClientInvoice(id, data, files, filesToDetach);
    },
    getViewFileUrl: (entityId: number, fileId: number) =>
      `/crm-clients-invoices/invoice/${entityId}/files/${fileId}`,
    delete: async (id: number) => {
      const { deleteClientInvoice } = await import('../../services/mutations');
      return await deleteClientInvoice(id);
    },
  },

  // Detail Page Configuration
  detail: {
    subtitle: 'Invoice Details',
    notFoundTitle: 'Invoice Not Found',
    notFoundMessage: 'The invoice you are looking for does not exist or has been deleted.',
    notFoundBackText: 'Back to Invoices',
    defaultTitle: 'Invoice',
    getTitle: (item: ClientInvoice) => {
      return item.InvoiceNo || `Invoice #${item.invoiceId}`;
    },
  },

  // Utility Functions
  utils: {
    formatDate: (dateString?: string | null) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
    formatCurrency,
  },

  // Table Configuration
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: ClientInvoice) => {
      window.location.href = `/client-invoices/${item.invoiceId}`;
    },
    keyExtractor: (item: ClientInvoice) => item.invoiceId,
    getClientId: (item: ClientInvoice) => item.clientId ?? null,
  },

  // Empty States
  emptyStates: {
    noData: 'Get started by creating a new invoice.',
    noSearchResults: 'No invoices found matching your search criteria.',
    noFilterResults: 'No invoices match the selected filters.',
  },

  // Delete Configuration
  delete: {
    title: 'Delete Invoice',
    message: 'Are you sure you want to delete this invoice? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete invoice. Please try again.',
    handler: async (id: number) => {
      const { deleteClientInvoice } = await import('../../services/mutations');
      await deleteClientInvoice(id);
    },
  },
};

// Table Columns
export type ClientInvoicesListContext = {
  mutate?: () => void;
  onOpenPaymentRemindersPanel?: (item: ClientInvoice) => void;
};

export const getColumns = (
  formatDate: (dateString?: string) => string,
  _formatCurrencyOptional?: unknown,
  colors?: ThemeColors,
  listContext?: ClientInvoicesListContext
): Column<ClientInvoice>[] => {
  const textColor = colors?.text || 'text-gray-900';
  const textSecondary = colors?.textSecondary || 'text-gray-700';

  const attachmentIcon = (
    <svg className="w-4 h-4 shrink-0 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
    </svg>
  );

  return [
  {
    key: 'InvoiceDate',
    header: 'Invoice Date',
    sortable: true,
    render: (value) => formatDate(value),
  },
  {
    key: 'invoiceTypeDescr',
    header: 'Type',
    sortable: true,
    render: (_value, row) => (
      row.invoiceTypeDescr?.pit_abbrev ?? ''
    ),
  },
  {
    key: 'client.clientName',
    header: 'Client',
    sortable: true,
    render: (value, row) => {
      const clientName = row.client?.clientName || '-';
      const attachmentCount = row.FileAttachment?.length ?? 0;
      const hasAttachments = attachmentCount > 0;
      return (
        <div className="flex items-center gap-1.5 min-w-0">
          <TruncatedText
            text={clientName}
            maxLength={50}
            showTooltip={true}
          />
          {hasAttachments && (
            <span
              title={`${attachmentCount} file${attachmentCount !== 1 ? 's' : ''} attached`}
              className="shrink-0 inline-flex text-gray-500 hover:text-gray-700"
            >
              {attachmentIcon}
            </span>
          )}
        </div>
      );
    },
  },
  {
    key: 'InvoiceNo',
    header: 'Invoice No.',
    sortable: true,
    render: (value) => (
      value || '-'
    ),
  },
  {
    key: 'invoiceAmount',
    header: 'Amount',
    sortable: true,
    render: (value) => formatCurrency(value),
  },
  {
    key: 'invoiceIncome',
    header: 'Income',
    sortable: true,
    render: (value) => formatCurrency(value),
  },
  {
    key: 'status',
    header: 'Status',
    sortable: true,
    render: (_value, row) => {
      const invoiceAmount = parseFloat(String(row.invoiceAmount || 0));
      const paymentTotal = row.paymentTotal !== null ? parseFloat(String(row.paymentTotal)) : null;
      const { label, kind } = getPaymentStatus(paymentTotal, invoiceAmount, formatCurrency);
      const isFullyPaid = paymentTotal !== null && paymentTotal >= invoiceAmount;
      const showFullPaymentBtn = !isFullyPaid && invoiceAmount > 0;

      const handleFullPayment = async (e: React.MouseEvent) => {
        e.stopPropagation();
        const paymentDate = row.InvoiceDate
          ? (typeof row.InvoiceDate === 'string' ? row.InvoiceDate : new Date(row.InvoiceDate).toISOString())
          : new Date().toISOString();
        try {
          const { createInvoicePayment } = await import('../../services/mutations');
          await createInvoicePayment({
            invoiceId: row.invoiceId,
            paymentDate,
            paymentAmount: row.invoiceAmount ?? 0,
          });
          listContext?.mutate?.();
          toast.success('Full payment recorded');
        } catch (err: unknown) {
          const message = err && typeof err === 'object' && 'message' in err ? String((err as { message: unknown }).message) : 'Failed to record payment';
          toast.error(message);
        }
      };

      const paymentDateParam = row.InvoiceDate
        ? `&paymentDate=${encodeURIComponent(typeof row.InvoiceDate === 'string' ? row.InvoiceDate : new Date(row.InvoiceDate).toISOString())}`
        : '';
      const clientIdParam = row.clientId != null ? `&clientId=${row.clientId}` : '';
      const invoiceAmountParam =
        row.invoiceAmount != null && row.invoiceAmount !== undefined
          ? `&invoiceAmount=${encodeURIComponent(String(row.invoiceAmount))}`
          : '';
      const paidTotal =
        row.paymentTotal !== null && row.paymentTotal !== undefined ? Number(row.paymentTotal) : 0;
      const invoicePaymentsTotalParam = `&invoicePaymentsTotal=${encodeURIComponent(String(Number.isFinite(paidTotal) ? paidTotal : 0))}`;
      const newPaymentUrl = `/invoice-payments/new?invoiceId=${row.invoiceId}${clientIdParam}${invoiceAmountParam}${invoicePaymentsTotalParam}${paymentDateParam}`;

      return (
        <div className="flex items-center gap-1.5 flex-wrap">
          {renderInvoicePaymentStatusBadge(label, kind)}
          {showFullPaymentBtn && (
            <>
              <button
                type="button"
                onClick={handleFullPayment}
                className="inline-flex items-center justify-center p-1.5 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded transition-colors"
                title="Record full payment"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
              <Link
                to={newPaymentUrl}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center justify-center p-1.5 text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded transition-colors"
                title="Record partial payment"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </Link>
            </>
          )}
        </div>
      );
    },
  },
  {
    key: 'invoiceDue',
    header: 'Due Date',
    sortable: true,
    render: (value, row) => {
      const invoiceAmount = parseFloat(String(row.invoiceAmount || 0));
      const paymentTotal = row.paymentTotal !== null ? parseFloat(String(row.paymentTotal)) : null;
      const isFullyPaid = paymentTotal !== null && paymentTotal >= invoiceAmount;
      const dueDate = value ? dayjs(value) : null;
      const isOverdue = !isFullyPaid && dueDate && dueDate.isBefore(dayjs());
      const overdueDays = isOverdue && dueDate ? dayjs().diff(dueDate, 'day') : null;

      return (
        <div>
          <span className={isOverdue ? `text-sm text-red-600 font-medium` : `text-sm ${textColor}`}>
            {formatDate(value)}
          </span>
          {overdueDays !== null && overdueDays > 0 && (
            <div className="text-xs text-red-600">
              {overdueDays} days overdue
            </div>
          )}
        </div>
      );
    },
  },
  {
    key: 'paymentReminders',
    header: 'Payment Reminders',
    sortable: false,
    render: (_value, item) => {
      const reminders = item.paymentReminders ?? [];
      const count = reminders.length;
      const last = reminders[0];
      const lastDate = last?.contactedAt ? formatDate(last.contactedAt) : null;
      const lastStatusValue = last?.responseStatus ?? null;
      const responseDate = last?.responseAt ? formatDate(last.responseAt) : null;
      const onOpenPanel = listContext?.onOpenPaymentRemindersPanel;
      const textMuted = colors?.textMuted ?? 'text-gray-500 dark:text-gray-400';
      if (count === 0) {
        if (onOpenPanel) {
          return (
            <button type="button" onClick={(e) => { e.stopPropagation(); e.preventDefault(); onOpenPanel(item); }} className="text-indigo-600 hover:text-indigo-800 hover:underline text-left">
              0 reminders
            </button>
          );
        }
        return '-';
      }
      const responseLine = lastStatusValue ? (
        <div className="flex flex-wrap items-center gap-1.5">
          {renderInvoicePaymentReminderStatusBadge(lastStatusValue)}
          {responseDate && <span className={cn('text-xs', textMuted)}>{responseDate}</span>}
        </div>
      ) : (
        <div className={cn('text-xs', textMuted)}>—</div>
      );
      const contactedLine = lastDate && (
        <div className={cn('text-xs mt-0.5', textMuted)}>Contacted {lastDate} · {count} reminder{count !== 1 ? 's' : ''}</div>
      );
      if (onOpenPanel) {
        return (
          <div className="text-left">
            <button type="button" onClick={(e) => { e.stopPropagation(); e.preventDefault(); onOpenPanel(item); }} className="text-left w-full hover:bg-gray-50 dark:hover:bg-gray-800/50 rounded px-1 -mx-1 py-0.5">
              {responseLine}
              {contactedLine}
            </button>
          </div>
        );
      }
      return (
        <div className="text-left">
          {responseLine}
          {contactedLine}
        </div>
      );
    },
  },
  ];
};

// Detail Sections
export const getDetailSections = (
  item: ClientInvoice,
  formatDate: (dateString?: string) => string
) => {
  const invoiceAmount = parseFloat(String(item.invoiceAmount || 0));
  const paymentTotal = item.paymentTotal !== null ? parseFloat(String(item.paymentTotal)) : null;
  const paymentStatus = getPaymentStatus(paymentTotal, invoiceAmount, formatCurrency);

  return [
    {
      title: 'Basic Information',
      icon: '📄',
      fields: [
        {
          label: 'Client',
          value: item.client?.clientName || `Client #${item.clientId}` || '-',
        },
        {
          label: 'Invoice Number',
          value: item.InvoiceNo || '-',
        },
        {
          label: 'Invoice Date',
          value: formatDate(item.InvoiceDate),
        },
        {
          label: 'Due Date',
          value: formatDate(item.invoiceDue),
        },
        {
          label: 'Status',
          value: renderInvoicePaymentStatusBadge(paymentStatus.label, paymentStatus.kind),
        },
      ],
    },
    {
      title: 'Type & Category',
      icon: '🏷️',
      fields: [
        {
          label: 'Type',
          value: item.invoiceTypeDescr?.pit_abbrev ?? '',
        },
        {
          label: 'Category',
          value: item.invoiceCategoryDescr?.pic_descr || '-',
        },
        {
          label: 'Support Agreement',
          value: item.supportAgreement
            ? `${item.supportAgreement.description || ''} (${item.supportAgreement.agreementType || ''})`.trim() || `Agreement #${item.supportAgreement.id}`
            : '-',
        },
      ],
    },
    {
      title: 'Financial Information',
      icon: '💰',
      fields: [
        {
          label: 'Amount',
          value: formatCurrency(item.invoiceAmount),
        },
        {
          label: 'Discount',
          value: item.invoiceDiscount ? `${item.invoiceDiscount}%` : '-',
        },
        {
          label: 'VAT',
          value: formatCurrency(item.invoiceVAT),
        },
        {
          label: 'Income',
          value: formatCurrency(item.invoiceIncome),
        },
        {
          label: 'Payment Total',
          value: formatCurrency(paymentTotal),
        },
      ],
    },
    {
      title: 'Details',
      icon: '📝',
      fields: [
        {
          label: 'Description',
          value: item.invoiceDescription || '-',
        },
        {
          label: 'Notes',
          value: item.invoiceNotes || '-',
        },
      ],
    },
    {
      title: 'Attachments',
      icon: '📎',
      fields: [
        {
          label: '',
          value: React.createElement(EntityAttachmentsSection, {
            downloadEndpoint: `/crm-clients-invoices/invoice/${item.invoiceId}/files`,
            files: item.FileAttachment || [],
            defaultDownloadName: `invoice_${item.invoiceId}_files.zip`,
            getViewFileUrl: (fileId) => `/crm-clients-invoices/invoice/${item.invoiceId}/files/${fileId}`,
          }),
        },
      ],
    },
    {
      title: 'Payment Reminders',
      icon: '🔔',
      fields: [
        { label: '', value: React.createElement(ClientInvoicePaymentRemindersSection, { invoiceId: item.invoiceId, variant: 'panel' }) },
      ],
    },
  ];
};

// Status Badges
export const getStatusBadges = (item: ClientInvoice, _colors?: ThemeColors) => {
  const invoiceAmount = parseFloat(String(item.invoiceAmount || 0));
  const paymentTotal = item.paymentTotal !== null ? parseFloat(String(item.paymentTotal)) : null;
  const { label, kind } = getPaymentStatus(paymentTotal, invoiceAmount);
  return renderInvoicePaymentStatusBadge(label, kind, 'px-3 py-1 text-sm font-semibold');
};

// Initial Form Data
export const getInitialFormData = (): Partial<ClientInvoice> => {
  return {
    clientId: undefined,
    invoiceType: undefined,
    InvoiceCategoryId: undefined,
    InvoiceDate: undefined,
    invoiceDue: undefined,
    InvoiceNo: '',
    invoiceDescription: '',
    invoiceAmount: undefined,
    invoiceDiscount: undefined,
    invoiceVAT: undefined,
    invoiceIncome: undefined,
    invoiceNotes: '',
    supportAgreementId: undefined,
  };
};

// Map Item to Form Data
export const mapItemToFormData = (item: ClientInvoice): Partial<ClientInvoice> => {
  return {
    clientId: item.clientId,
    invoiceType: item.invoiceType,
    InvoiceCategoryId: item.InvoiceCategoryId,
    InvoiceDate: item.InvoiceDate,
    invoiceDue: item.invoiceDue,
    InvoiceNo: item.InvoiceNo || '',
    invoiceDescription: item.invoiceDescription || '',
    invoiceAmount: item.invoiceAmount,
    invoiceDiscount: item.invoiceDiscount,
    invoiceVAT: item.invoiceVAT,
    invoiceIncome: item.invoiceIncome,
    invoiceNotes: item.invoiceNotes || '',
    supportAgreementId: item.supportAgreementId,
  };
};

// Toolbar Icons
export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'download':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      );
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    default:
      return null;
  }
};

// Table Actions
export const getTableActions = (
  item: ClientInvoice,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.invoiceId)}
      editTo={moduleConfig.module.route.edit(item.invoiceId)}
      onDelete={() => onDelete(item.invoiceId)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};

